import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import BrandLogo from '../../components/BrandLogo';
import { ArrowLeft, Mail, Sparkles, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { formatApiErrorDetail, extractApiErrorCode } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post(`${API}/api/auth/forgot-password`, { email });
            setSent(true);
        } catch (err) {
            const detail = err.response?.data?.detail;
            const code = extractApiErrorCode(detail);
            const localized = code
                ? t(`auth.errors.${code}`, { defaultValue: formatApiErrorDetail(detail) || t('auth.errors.generic') })
                : (formatApiErrorDetail(detail) || t('auth.errors.generic'));
            setError(localized);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-8" data-testid="forgot-password-page">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <Link to="/" className="inline-flex items-center gap-3 justify-center">
                        <BrandLogo size={40} />
                        <span className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">GUIRETAL</span>
                    </Link>
                </div>

                <div className="bg-white p-8 rounded-lg border border-[#E5E7EB] shadow-sm">
                    {sent ? (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-[#D1FAE5] rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-[#10B981]" />
                            </div>
                            <h2 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                                {t('auth.forgot_sent_title')}
                            </h2>
                            <p className="text-[#4B5563] mt-2">
                                {t('auth.forgot_sent_message')}
                            </p>
                            <Link to="/login">
                                <Button className="mt-6 bg-[#002FA7] hover:bg-[#002480] text-white">
                                    {t('auth.forgot_back_to_login')}
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                                    {t('auth.forgot_title')}
                                </h2>
                                <p className="text-[#4B5563] mt-2">
                                    {t('auth.forgot_subtitle')}
                                </p>
                            </div>

                            {error && (
                                <div className="mb-6 p-4 bg-[#FEE2E2] border border-[#EF4444]/20 rounded-lg">
                                    <p className="text-[#DC2626] text-sm">{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-[#0A0A0A]">{t('auth.email')}</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder={t('auth.placeholder_email_login')}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            className="pl-10 border-[#E5E7EB] focus:border-[#002FA7] focus:ring-[#002FA7]/10"
                                            data-testid="forgot-email-input"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-[#002FA7] hover:bg-[#002480] text-white h-11"
                                    data-testid="forgot-submit-btn"
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-2">
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            {t('auth.forgot_sending')}
                                        </span>
                                    ) : (
                                        t('auth.forgot_send_link')
                                    )}
                                </Button>
                            </form>

                            <div className="mt-6 text-center">
                                <Link to="/login" className="text-[#002FA7] hover:underline flex items-center justify-center gap-2">
                                    <ArrowLeft className="w-4 h-4" />
                                    {t('auth.forgot_back_to_login')}
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
